import {
  ArrowDown, ArrowUp, CheckCircle, Download, Link, Mail, RotateCcw, ShoppingBag, Trash, XCircle,
} from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useState } from 'react';

import { GetParticipantOrdersQuery as GetParticipantOrders, Permission } from '__generated__/graphql';

import { useSession } from '../../../Auth/SessionProvider';
import ExtraInfoSection from './ExtraInfoSection';
import GetOrderQuery from './GetOrderQuery';
import InvoiceSection from './InvoiceSection';
import PaymentDetails from './PaymentDetails';
import PurchaseDetails, { Cell, Container } from './PurchaseDetails';
import StatusLabels from './StatusLabels';
import UI from '../../../../common/UI';
import useCancelOrderModal from './useCancelOrderModal';
import useDeleteOrderModal from './useDeleteOrderModal';
import useLocale from '../../../../common/useLocale';
import useMediaDevice from '../../../../common/useMediaDevice';
import usePaymentLinkModal from './usePaymentLinkModal';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useRefundOrderModal from './useRefundOrderModal';
import useRestoreOrderModal from './useRestoreOrderModal';
import useScroll from '../../../../common/useScroll';
import useSendConfirmationModal from './useSendConfirmationModal';

type Participant = GetParticipantOrders['project']['participant'];
type Order = Participant['orders'][0];

const tableColumns = '10fr 5fr 7fr 3fr 6fr 52px';

interface OrderDetailsPreviewProps {
  order: Order;
}

const OrderDetailsPreview = ({ order }: OrderDetailsPreviewProps) => {
  const { formatCurrency, formatDate, parseDate } = useLocale();

  return (
    <UI.GridContainer
      sc={{ alignVertical: 'center', columns: tableColumns, gutter: 0 }}
    >
      <UI.Div sc={{ pr: 1 }} style={{ minWidth: 0 }}>
        <UI.H4>
          <UI.Truncate>
            {order.invoice_id}
          </UI.Truncate>
        </UI.H4>
      </UI.Div>
      <UI.FlexContainer sc={{ px: 1, color: 'gray.500' }} style={{ minWidth: 0, gridColumn: '2 / span 2' }}>
        <UI.Label sc={{ small: true }} style={{ minWidth: 0 }}>
          <UI.Truncate>
            {order.event.title}
          </UI.Truncate>
        </UI.Label>
      </UI.FlexContainer>
      <UI.FlexContainer
        sc={{ justifyContent: 'space-between', alignItems: 'center', spaceX: 2, px: 1 }}
        style={{ gridColumn: '4 / span 3' }}
      >
        <UI.Div sc={{ color: 'gray.500' }} style={{ alignSelf: 'start', marginRight: 'auto' }}>
          {formatCurrency(order.amount)}
        </UI.Div>
        <UI.Div style={{ marginLeft: 'auto' }}>
          <StatusLabels
            amount={order.amount}
            remainingAmount={order.remaining_amount}
            refundedAmount={order.refunded_amount}
            balance={order.balance}
            cancelled={order.cancelled}
            waitlist={order.waitlist}
            preregistration={order.preregistration}
            sc={{ my: -0.5 }}
          />
        </UI.Div>
        <UI.Span sc={{ color: 'gray.500' }} style={{ minWidth: 80 }}>
          {formatDate(
            parseDate(order.invoice_date, { format: 'internal_date' }),
            { format: 'display_compact_date' },
          )}
        </UI.Span>
      </UI.FlexContainer>
    </UI.GridContainer>
  );
};

interface OrderBodyProps {
  orderSummary: Order;
  perPage?: number;
}

const OrderDetailsBody = ({ orderSummary, perPage = 25 }: OrderBodyProps) => {
  const { t } = useTranslation();
  const { formatCurrency, formatDate, parseDate } = useLocale();
  const { scrollTo } = useScroll();
  const { hasPermission } = useSession();
  const device = useMediaDevice();
  const project = useProject();

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);
  const hasRefundPermission = hasPermission(project.id, [Permission.refunds]);

  const [ticketsLimit, setTicketsLimit] = useState(perPage);

  const showMore = () => {
    setTicketsLimit((limit) => limit + perPage);
  };

  const showLess = () => {
    scrollTo(`OrderDetails_${order.id}`, { gutter: 1 });
    setTicketsLimit(perPage);
  };

  const { data, loading } = useQuery(
    GetOrderQuery,
    {
      variables: {
        order_id: orderSummary.id,
        tickets_limit: ticketsLimit,
      },
    },
  );

  const order = data?.order;
  const purchases = order?.purchases;
  const payments = order?.payments.data.filter((payment) => payment.status === 'paid') || [];

  const [openSendConfirmationModal] = useSendConfirmationModal({
    order,
  });

  const [openPaymentLinkModal] = usePaymentLinkModal({
    order,
  });

  const [openRefundOrderModal] = useRefundOrderModal({
    orderId: order?.id,
  });

  const [openCancelOrderModal] = useCancelOrderModal({
    order,
  });

  const [openRestoreOrderModal] = useRestoreOrderModal({
    order,
  });

  const [openDeleteOrderModal] = useDeleteOrderModal({
    order,
  });

  if (!order) {
    return <UI.PageLoader />;
  }

  const passedOnFee = order.paid_passed_on_fee + order.remaining_passed_on_fee;

  return (
    <UI.GridContainer id={`OrderDetails_${order.id}`}>
      <UI.FlexContainer sc={{ wrap: 'wrap', mt: -1, mr: -1 }}>
        {hasEditPermission && (
          <UI.Button
            onClick={() => openSendConfirmationModal()}
            sc={{ size: 'sm', mt: 1, mr: 1 }}
          >
            <UI.Icon>
              <Mail />
            </UI.Icon>
            {' '}
            {t('send_confirmation')}
          </UI.Button>
        )}
        {order.invoice_url && (
          <UI.AButton
            href={order.invoice_url}
            target="_blank"
            rel="noopener noreferrer"
            sc={{ size: 'sm', mt: 1, mr: 1 }}
          >
            <UI.Icon>
              <Download />
            </UI.Icon>
            {' '}
            {t('download_invoice')}
          </UI.AButton>
        )}
        {order.remaining_amount > 0 && (
          <UI.Button
            onClick={() => openPaymentLinkModal()}
            sc={{ size: 'sm', mt: 1, mr: 1 }}
          >
            <UI.Icon>
              {/* eslint-disable-next-line */}
              <Link />
            </UI.Icon>
            {' '}
            {t('get_payment_link')}
          </UI.Button>
        )}
        {hasRefundPermission && (order.paid_amount - order.refunded_amount) > 0 && (
          <UI.Button
            onClick={() => openRefundOrderModal()}
            sc={{ size: 'sm', mt: 1, mr: 1 }}
          >
            <UI.Icon>
              <RotateCcw />
            </UI.Icon>
            {' '}
            {t('refund')}
          </UI.Button>
        )}
        {hasEditPermission && !orderSummary.cancelled && (
          <UI.Button
            onClick={() => openCancelOrderModal()}
            sc={{ size: 'sm', mt: 1, mr: 1 }}
          >
            <UI.Icon>
              <XCircle />
            </UI.Icon>
            {' '}
            {t('cancel_order')}
          </UI.Button>
        )}
        {hasEditPermission && orderSummary.cancelled && (
          <UI.Button
            onClick={() => openRestoreOrderModal()}
            sc={{ size: 'sm', mt: 1, mr: 1 }}
          >
            <UI.Icon>
              <CheckCircle />
            </UI.Icon>
            {' '}
            {t('restore_order')}
          </UI.Button>
        )}
        {hasEditPermission && orderSummary.deletable && (
          <UI.Button
            onClick={() => openDeleteOrderModal()}
            sc={{ size: 'sm', mt: 1, mr: 1 }}
          >
            <UI.Icon>
              <Trash />
            </UI.Icon>
            {' '}
              {t('delete_order')}
          </UI.Button>
        )}
      </UI.FlexContainer>

      <UI.GridContainer sc={{ gutter: 0.25 }}>
        {!device.isSmall && (
          <UI.GridContainer sc={{ columns: tableColumns, gutter: 0 }}>
            <UI.Legend sc={{ pr: 1 }} style={{ gridColumn: '1 / span 2' }}>
              {t('purchase')}
            </UI.Legend>
            <UI.Legend sc={{ px: 1 }}>
              {t('contact')}
            </UI.Legend>
            <UI.Legend sc={{ px: 1 }}>
              {t('amount')}
            </UI.Legend>
            <UI.Legend sc={{ px: 1, textAlign: 'right' }}>
              {t('status')}
            </UI.Legend>
          </UI.GridContainer>
        )}

        <UI.Div>
          {purchases.map((purchase, index) => (
            <UI.Div key={purchase.id}>
              <UI.HR
                sc={{
                  borderWidth: (
                    !purchase.registration || purchase.registration.id !== purchases[index - 1]?.registration?.id
                  ) ? 2 : 1,
                  dashed: !purchase.ticket && purchase.registration
                    && purchase.registration.id === purchases[index - 1]?.registration?.id,
                  my: 0,
                }}
              />
              <PurchaseDetails
                purchase={purchase}
                order={order}
                tableColumns={tableColumns}
              />
            </UI.Div>
          ))}
          {passedOnFee > 0 && (
            <>
              <UI.HR sc={{ my: 0 }} />
              <Container sc={{ tableColumns }}>
                <Cell sc={{ flexSelf: '75%' }}>
                  <UI.Div>
                    <UI.Span sc={{ color: 'gray.800' }}>
                      {t('platform_fee')}
                    </UI.Span>
                  </UI.Div>
                </Cell>

                {!device.isSmall && (
                  <>
                    <Cell />
                    <Cell />
                  </>
                )}

                <Cell
                  sc={{
                    flexSelf: '25%',
                    flex: device.isSmall && { direction: 'column', alignItems: 'flex-end' },
                  }}
                >
                  {formatCurrency(passedOnFee)}
                </Cell>

                <Cell sc={{ py: 1.5 }}>
                  <StatusLabels
                    amount={passedOnFee}
                    remainingAmount={order.remaining_passed_on_fee}
                    refundedAmount={0}
                    balance={0}
                    cancelled={false}
                    waitlist={order.waitlist}
                    preregistration={order.preregistration}
                    sc={{ justifyContent: [undefined, 'flex-end'] }}
                  />
                </Cell>

                <Cell />
              </Container>
            </>
          )}
          <UI.HR sc={{ borderWidth: 2, my: 0 }} />
        </UI.Div>
      </UI.GridContainer>

      {(order.purchases_count > order.purchases.length || ticketsLimit > perPage) && (
        <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <UI.Div>
            <UI.Button
              onClick={() => showMore()}
              sc={{
                outline: true,
                brand: 'gray.300',
                color: 'gray.800',
                loading,
              }}
              disabled={loading || order.purchases.length >= order.purchases_count}
            >
              <UI.Icon>
                <ArrowDown />
              </UI.Icon>
              {' '}
              {t('show_more')}
            </UI.Button>
            {' '}
            {ticketsLimit > perPage && !loading && (
              <UI.Button
                onClick={() => showLess()}
                disabled={loading}
                sc={{ blank: true }}
              >
                <UI.Icon>
                  <ArrowUp />
                </UI.Icon>
                {' '}
                {t('show_less')}
              </UI.Button>
            )}
          </UI.Div>
          <UI.Div sc={{ muted: true }}>
            {t('showing_x_out_of_y_purchases', { x: order.purchases.length, y: order.purchases_count })}
          </UI.Div>
        </UI.FlexContainer>
      )}

      <UI.GridContainer sc={{ gutter: 0.5, columns: ['1fr', '1fr 3fr'] }}>
        <UI.Legend>{t('invoice_details')}</UI.Legend>

        <InvoiceSection order={order} />
      </UI.GridContainer>

      <UI.HR />

      {order.participant_fields.length > 0 && (
        <>
          <UI.GridContainer sc={{ gutter: 0.5, columns: ['1fr', '1fr 3fr'] }}>
            <UI.Legend>{t('extra_information')}</UI.Legend>

            <ExtraInfoSection order={order} />
          </UI.GridContainer>

          <UI.HR />
        </>
      )}

      <UI.GridContainer sc={{ gutter: 0.5, columns: ['1fr', '1fr 3fr'] }}>
        <UI.Legend>
          {t('history')}
        </UI.Legend>
        <UI.Div>
          <UI.GridContainer sc={{ gutter: 0.5 }}>
            <UI.FlexContainer sc={{ alignItems: 'center' }}>
              <UI.Div
                sc={{ mr: 3, flex: { alignItems: 'center', justifyContent: 'center' }, background: 'secondary.100' }}
                style={{ width: 32, height: 24, borderRadius: 3 }}
              >
                <UI.Icon sc={{ color: 'secondary.900' }} style={{ opacity: 0.7 }}>
                  <ShoppingBag />
                </UI.Icon>
              </UI.Div>

              <UI.Div sc={{ color: 'gray.400' }}>
                <Trans
                  i18nKey="order_placed_on_date"
                  values={{
                    date: formatDate(
                      parseDate(order.created_at, { timezone: 'UTC' }),
                      { format: 'display_compact_date_time' },
                    ),
                  }}
                >
                  <UI.Strong sc={{ color: 'gray.800' }} />
                </Trans>
              </UI.Div>
            </UI.FlexContainer>
            {payments.map((payment) => (
              <PaymentDetails payment={payment} key={payment.id} />
            ))}
          </UI.GridContainer>
        </UI.Div>
      </UI.GridContainer>
    </UI.GridContainer>
  );
};

export interface OrderDetailsProps {
  order: Order
  participant: Participant;
  isActive: boolean;
  isMuted: boolean;
}

const OrderDetails = ({ order, participant, isActive, isMuted }: OrderDetailsProps) => {
  const { t } = useTranslation();
  const project = useProject();

  return (
    <UI.CollapsibleNavCard
      to={`/${project.id}/participants/${participant.id}/orders/${isActive ? '' : order.id}`}
      open={isActive}
      muted={isMuted}
      preview={<OrderDetailsPreview order={order} />}
      body={<OrderDetailsBody orderSummary={order} />}
      ariaLabel={`${t('order')} ${order.invoice_id} • ${order.event.title}`}
    />
  );
};

export default OrderDetails;
